
import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Eye, Clock, TrendingUp, AlertTriangle, CheckCircle, Settings, RefreshCw } from 'lucide-react';
import { 
  getUserBehaviorAnalyticsData, 
  UserBehaviorAnalyticsData,
  formatSessionDuration 
} from '@/utils/user-behavior-helpers';
import { useToast } from '@/hooks/use-toast';
import WorldMap from '@/components/admin/data/WorldMap';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28AFF', '#FF6E6E'];

const UserBehaviorAnalysis = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [deviceTab, setDeviceTab] = useState('device');
  const [analyticsData, setAnalyticsData] = useState<UserBehaviorAnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCredentialsSetup, setShowCredentialsSetup] = useState(false);
  const { toast } = useToast();

  const fetchAnalyticsData = async (showToast = false) => {
    try {
      if (showToast) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      
      const data = await getUserBehaviorAnalyticsData(timeRange);
      setAnalyticsData(data);
      
      // Show different messages based on data source
      if (data.dataSource === 'google_analytics') {
        console.log('Displaying real data from Google Analytics');
        setShowCredentialsSetup(false);
        if (showToast) {
          toast({
            title: "Data Refreshed",
            description: "Analytics data has been updated with latest information"
          });
        }
      } else if (data.dataSource === 'google_analytics_with_booking_geo') {
        console.log('Displaying GA4 data with real booking geography');
        setShowCredentialsSetup(false);
        if (showToast) {
          toast({
            title: "Data Refreshed",
            description: "Analytics updated with latest GA4 data and real booking geography"
          });
        }
      } else if (data.dataSource === 'fallback_with_booking_geo') {
        console.log('Displaying simulated GA4 data with real booking geography');
        setShowCredentialsSetup(false);
        if (showToast) {
          toast({
            title: "Data Refreshed", 
            description: "Using simulated web data but real booking geography"
          });
        }
      } else if (data.dataSource === 'fallback_simulation') {
        console.log('Displaying simulated data (GA4 connection failed)');
        if (data.error?.includes('Google Analytics credentials not found')) {
          setShowCredentialsSetup(true);
        } else {
          setShowCredentialsSetup(false);
        }
        // Only show error toast if there's actually an error, not for successful fallback data
        if (data.error && showToast && !data.dataSource.includes('google_analytics')) {
          toast({
            variant: "default",
            title: "Using Simulated Data",
            description: "Google Analytics connection failed, displaying simulated data"
          });
        }
      }
    } catch (error) {
      console.error('Error fetching user behavior analytics:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Unable to load user behavior analytics data"
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const handleRefresh = () => {
    fetchAnalyticsData(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-80 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight mb-2">User Behavior Analysis</h1>
        <p className="text-muted-foreground">
          Analyze user behavior patterns and booking conversion funnel.
        </p>
      </div>

      {/* Google Analytics Credentials Setup Alert - only show for fallback data */}
      {showCredentialsSetup && analyticsData && (analyticsData.dataSource === 'fallback_simulation' || analyticsData.dataSource === 'fallback_with_booking_geo') && (
        <Alert className="border-blue-200 bg-blue-50">
          <Settings className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <div className="flex items-center justify-between">
              <span>Google Analytics service account credentials need to be configured to fetch real data</span>
              <Button 
                onClick={() => window.open('https://supabase.com/dashboard/project/tgnzqtzbydztcfhqmxxh/settings/functions', '_blank')}
                size="sm"
                variant="outline"
                className="ml-4"
              >
                Configure Credentials
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Data Source Status Indicator */}
      {analyticsData && (analyticsData.dataSource === 'google_analytics' || analyticsData.dataSource === 'google_analytics_with_booking_geo') && (
        <Alert className="border-green-200 bg-green-50">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              Displaying real data from Google Analytics 4
              {analyticsData.dataSource === 'google_analytics_with_booking_geo' && ' with real booking geography'}
            </AlertDescription>
          </div>
        </Alert>
      )}
      
      {analyticsData && 
       analyticsData.dataSource !== 'google_analytics' && 
       analyticsData.dataSource !== 'google_analytics_with_booking_geo' && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              Displaying simulated data (Google Analytics connection failed)
              {analyticsData.error && ` - Error: ${analyticsData.error}`}
            </AlertDescription>
          </div>
        </Alert>
      )}

      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Time Range</SelectLabel>
                <SelectItem value="week">Past 7 days</SelectItem>
                <SelectItem value="month">Past 30 days</SelectItem>
                <SelectItem value="quarter">Past 90 days</SelectItem>
                <SelectItem value="year">Past 365 days</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        
        <Button 
          onClick={handleRefresh}
          disabled={isRefreshing}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </div>

      {/* Key Metrics Summary */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Page Views</CardTitle>
            <Eye className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsData?.totalPageViews.toLocaleString() || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Average Session Duration</CardTitle>
            <Clock className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatSessionDuration(analyticsData?.averageSessionDuration || 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Bounce Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsData?.bounceRate || 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Booking Conversion Funnel</CardTitle>
            <CardDescription>
              User journey from homepage to completed booking
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={analyticsData?.conversionRates || []}
                  layout="vertical"
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" unit="%" />
                  <YAxis dataKey="name" type="category" width={200} />
                  <Tooltip formatter={(value) => [`${Number(value).toFixed(1)}%`, "Conversion Rate"]} />
                  <Legend />
                  <Bar dataKey="rate" fill="#8884d8" name="Conversion Rate (%)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Page Time Analysis</CardTitle>
            <CardDescription>
              Time users spend on each step of the booking journey
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={analyticsData?.pageTimes || []}
                  layout="vertical"
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" unit="s" />
                  <YAxis dataKey="name" type="category" width={150} />
                  <Tooltip formatter={(value) => [`${value} seconds`, "Average Time"]} />
                  <Legend />
                  <Bar dataKey="time" fill="#82ca9d" name="Time (seconds)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Traffic Sources</CardTitle>
            <CardDescription>
              User visit sources
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analyticsData?.trafficSources || []}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {(analyticsData?.trafficSources || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value}%`, "Percentage"]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Device and Browser Analysis</CardTitle>
            <CardDescription>
              User device and browser preferences
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={deviceTab} onValueChange={setDeviceTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="device">Device Type</TabsTrigger>
                <TabsTrigger value="browser">Browser Type</TabsTrigger>
              </TabsList>
              <TabsContent value="device">
                <div className="h-72 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analyticsData?.deviceTypes || []}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {(analyticsData?.deviceTypes || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value}%`, "Percentage"]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
              <TabsContent value="browser">
                <div className="h-72 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analyticsData?.browserTypes || []}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {(analyticsData?.browserTypes || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value}%`, "Percentage"]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* World Map for Geographic Distribution */}
      {analyticsData?.geographicDistribution && (
        <Card>
          <CardHeader>
            <CardTitle>Geographic Distribution</CardTitle>
            <CardDescription>
              Visitor distribution across countries and regions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <WorldMap data={analyticsData.geographicDistribution} />
              
              {/* Geographic data table */}
              <div className="mt-6">
                <h4 className="font-semibold mb-3">Top Countries by Sessions</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {analyticsData.geographicDistribution.slice(0, 6).map((item, index) => (
                    <div key={item.country} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">{item.country}</div>
                        <div className="text-sm text-gray-600">{item.users} users</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{item.sessions}</div>
                        <div className="text-sm text-gray-600">{item.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserBehaviorAnalysis;
